package fyi.moko.hotpl.data.network

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import fyi.moko.hotpl.domain.model.Friendship
import fyi.moko.hotpl.domain.model.FriendSharedPlace
import fyi.moko.hotpl.domain.model.InviteCode
import fyi.moko.hotpl.domain.service.SocialGraphService
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

/**
 * GitLive Firestore implementation of [SocialGraphService] (Phase 3 Social).
 * Collections: `/invite_codes/{code}` and `/friendships/{lowUid}_{highUid}`, plus reads of
 * the existing `/public_places` projection for a friend's shared places.
 */
class FirebaseSocialDataSource(
    emulatorHost: String? = null,
) : SocialGraphService {
    private val firestore = Firebase.firestore

    init {
        if (emulatorHost != null) {
            try {
                firestore.useEmulator(emulatorHost, 8080)
            } catch (e: Exception) {
                // Ignore re-configuration errors, mirroring the place data source.
            }
        }
    }

    override suspend fun findMyInviteCode(uid: String): InviteCode? {
        // The rules only allow list queries filtered on ownerUid == auth.uid.
        val snapshot = firestore
            .collection("invite_codes")
            .where { "ownerUid" equalTo uid }
            .get()
        return snapshot.documents.firstNotNullOfOrNull { doc ->
            try {
                doc.data<InviteCodeDto>().toDomain(doc.id)
            } catch (e: Exception) {
                println("FirebaseSocialDataSource: invite code ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                null
            }
        }
    }

    override suspend fun createInviteCode(invite: InviteCode) {
        // No update rule exists on /invite_codes, so a set() on an existing doc is denied —
        // that server-side denial is the collision backstop behind the caller's exists() probe.
        firestore.document("invite_codes/${invite.code}").set(invite.toDto())
    }

    override suspend fun inviteCodeExists(code: String): Boolean =
        firestore.document("invite_codes/$code").get().exists

    override suspend fun deleteInviteCode(code: String) {
        firestore.document("invite_codes/$code").delete()
    }

    override suspend fun getInviteCode(code: String): InviteCode? {
        val doc = firestore.document("invite_codes/$code").get()
        if (!doc.exists) return null
        return try {
            doc.data<InviteCodeDto>().toDomain(doc.id)
        } catch (e: Exception) {
            println("FirebaseSocialDataSource: invite code $code failed to decode: ${e.message ?: e::class.simpleName}")
            null
        }
    }

    override suspend fun getFriendship(id: String): Friendship? {
        val doc = firestore.document("friendships/$id").get()
        if (!doc.exists) return null
        return try {
            doc.data<FriendshipDto>().toDomain(doc.id)
        } catch (e: Exception) {
            println("FirebaseSocialDataSource: friendship $id failed to decode: ${e.message ?: e::class.simpleName}")
            null
        }
    }

    override suspend fun createFriendship(friendship: Friendship) {
        firestore.document("friendships/${friendship.id}").set(friendship.toDto())
    }

    override fun observeFriendships(uid: String): Flow<List<Friendship>> =
        // array-contains on the caller's own uid is the only friendships list query the
        // rules prove (read rule: auth.uid in resource.data.uids).
        firestore.collection("friendships")
            .where { "uids" contains uid }
            .snapshots
            .map { snapshot ->
                snapshot.documents.mapNotNull { doc ->
                    try {
                        doc.data<FriendshipDto>().toDomain(doc.id)
                    } catch (e: Exception) {
                        println("FirebaseSocialDataSource: friendship doc ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                        null
                    }
                }
            }

    override suspend fun removeFriendship(friendshipId: String) {
        firestore.document("friendships/$friendshipId").delete()
    }

    override fun observeSharedPlaces(ownerUid: String): Flow<List<FriendSharedPlace>> =
        // ownerId equality must be the ONLY filter: the deployed read rule proves this list
        // query via its friendship-exists branch, which is decidable from the ownerId
        // constant alone. Adding e.g. a visibility filter is unnecessary (PRIVATE docs never
        // exist in this collection) and risks breaking that provability.
        firestore
            .collection("public_places")
            .where { "ownerId" equalTo ownerUid }
            .snapshots
            .map { snapshot ->
                snapshot.documents.mapNotNull { doc ->
                    try {
                        doc.data<PublicPlaceDto>().toFriendSharedPlace()
                    } catch (e: Exception) {
                        println("FirebaseSocialDataSource: shared place ${doc.id} failed to decode: ${e.message ?: e::class.simpleName}")
                        null
                    }
                }
            }
}
